import React from "react";
import { Space } from "antd";
import { AxProgress, AxDivider, AxButton } from "@dynata/axiom3";

class Progress extends React.Component {
  render() {
    return (
      <div className="section" id="progress">
        <h3>Progress</h3>
        <p>Display the current progress of an operation flow.</p>
        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>
        <Space direction="vertical">
          <Space size="large" direction="vertical">
            <pre>{`<AxProgress percent={25} />`}</pre>
            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/progress/#API"
                >
                  https://ant.design/components/progress/#API
                </AxButton>
              </p>
            </div>

            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>size</td>
                  <td>Default is medium</td>
                  <td>small | medium | large</td>
                  <td></td>
                </tr>
                <tr>
                  <td>type</td>
                  <td>string</td>
                  <td>"line | circle"</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showInfo</td>
                  <td>
                    Whether to display the progress value and the status icon
                  </td>
                  <td>boolean</td>
                  <td></td>
                </tr>
                <tr>
                  <td>labelLeft</td>
                  <td>Whether to display left label.</td>
                  <td>string</td>

                  <td></td>
                </tr>
                <tr>
                  <td>labelRight</td>
                  <td>Whether to display right label.</td>
                  <td>string</td>

                  <td></td>
                </tr>
                <tr>
                  <td>percent</td>
                  <td>To set the completion percentage</td>
                  <td>number</td>

                  <td></td>
                </tr>
                <tr>
                  <td>status</td>
                  <td></td>
                  <td>
                    string | <code>active</code> | <code>exception</code> |{" "}
                    <code>success</code>
                  </td>

                  <td></td>
                </tr>
                <tr>
                  <td>strokeLinecap</td>
                  <td>To set the style of the progress linecap</td>
                  <td>"round | square"</td>
                  <td></td>
                </tr>
                <tr>
                  <td>format</td>
                  <td>The template function of the content</td>
                  <td>function(percent, successPercent)</td>
                  <td>
                    (percent) =&gt; percent + <code>%</code>
                  </td>
                </tr>
                <tr>
                  <td>percent</td>
                  <td>To set the completion percentage</td>
                  <td>number</td>
                  <td>0</td>
                </tr>
                <tr>
                  <td>showInfo</td>
                  <td>
                    Whether to display the progress value and the status icon
                  </td>
                  <td>boolean</td>
                  <td>true</td>
                </tr>
                <tr>
                  <td>status</td>
                  <td>
                    To set the status of the Progress, options:{" "}
                    <code>success</code> <code>exception</code>{" "}
                    <code>normal</code> <code>active</code>(line only)
                  </td>
                  <td>string</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>strokeColor</td>
                  <td>The color of progress bar</td>
                  <td>string</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>strokeLinecap</td>
                  <td>To set the style of the progress linecap</td>
                  <td>
                    <code>round</code> | <code>butt</code> | <code>square</code>
                    , see{" "}
                    <a href="https://developer.mozilla.org/docs/Web/SVG/Attribute/stroke-linecap">
                      stroke-linecap
                    </a>
                  </td>
                  <td>
                    <code>round</code>
                  </td>
                </tr>
                <tr>
                  <td>success</td>
                  <td>Configs of successfully progress bar</td>
                  <td>
                    <pre>{`{ percent: number, strokeColor: string }`}</pre>
                  </td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>trailColor</td>
                  <td>The color of unfilled part</td>
                  <td>string</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>type</td>
                  <td>
                    To set the type, options: <code>line</code>{" "}
                    <code>circle</code> <code>dashboard</code>
                  </td>
                  <td>string</td>
                  <td>
                    <code>line</code>
                  </td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/progress/#API"
                      >
                        {`<Progress />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                </tr>
              </tbody>
            </table>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Status
        </AxDivider>

        <Space direction="vertical" size={40}>
          <pre>{`<AxProgress percent={25} />`}</pre>
          <AxProgress percent={25} />
          <AxProgress percent={50} status="active" />
          <AxProgress percent={75} status="exception" />
          <AxProgress percent={90} status="success" />
          <AxProgress percent={80} showInfo={false} size="small" />
          <AxProgress percent={80} size="small" />
          <AxProgress
            percent={25}
            showInfo={false}
            labelLeft="Label Left"
            labelRight="Label Right"
            size="large"
          />
        </Space>

        <AxDivider orientation="left" plain>
          Circular
        </AxDivider>

        <Space direction="vertical" size={40}>
          <pre>{`<AxProgress percent={30} type="circle" />`}</pre>
          <AxProgress percent={30} type="circle" />
          <AxProgress percent={50} type="circle" status="active" />
          <AxProgress percent={70} type="circle" status="exception" />
          <AxProgress percent={100} type="circle" />
          <AxProgress percent={80} type="circle" showInfo={false} />
          <AxProgress percent={100} type="circle" width={80} />
        </Space>

        <AxDivider orientation="left" plain>
          Square Line Cap
        </AxDivider>

        <Space direction="vertical" size={40}>
          <pre>{`<AxProgress percent={50} strokeLinecap="square" />`}</pre>
          <AxProgress percent={50} strokeLinecap="square" />
          <AxProgress percent={50} strokeLinecap="square" size="small" />
          <AxProgress
            percent={75}
            labelLeft="Label"
            size="large"
            strokeLinecap="square"
          />
          <AxProgress percent={50} strokeLinecap="square" type="circle" />
          <AxProgress
            percent={50}
            strokeLinecap="square"
            type="circle"
            width={80}
          />
        </Space>
      </div>
    );
  }
}
export default Progress;
